import type { LabelWithLocation, Place } from '@skop-app/lib-domain/types'
import { placeTypes } from '@skop-app/lib-domain/constants'
import { standardizeString } from '@skop-app/lib-domain/utils'

export const PLACE_CREATE_SENTINEL = 'create_new'
export const DEFAULT_PLACE_COUNTRY = 'France'

export type PlacePickerOption = LabelWithLocation & {
  address?: string
  country?: string
  name?: string
  organizationID?: string
  searchText?: string
  subtitle?: string
  type?: string
  typeLabel?: string
  zipcode?: string
}

export type PlaceOptionGroup = {
  label: string
  value: string
  options: PlacePickerOption[]
}

export type PlaceDirectoryFilters = {
  city?: string
  search?: string
  type?: string
  zipcode?: string
}

export type ParsedPostalCity = {
  city?: string
  zipcode?: string
}

const PLACE_TYPE_FALLBACK_LABEL = 'Autres lieux'
const UNKNOWN_PLACE_TYPE = '__unknown__'

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.replace(/\s+/g, ' ').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export const normalizePlaceMatcherPart = (value: string | undefined): string =>
  standardizeString(toTrimmedString(value) ?? '')

export const buildPlaceMatchingKey = (params: {
  address?: string
  city?: string
  country?: string
  zipcode?: string
}): string => {
  const { address, zipcode, city, country } = params
  return [address, zipcode, city, country]
    .map(normalizePlaceMatcherPart)
    .join('|')
}

export const formatPlaceSubtitle = (params: {
  address?: string
  city?: string
  country?: string
  zipcode?: string
}): string | undefined => {
  const { address, zipcode, city, country } = params
  const left = [toTrimmedString(address)].filter(Boolean).join(', ')
  const postalCity = [toTrimmedString(zipcode), toTrimmedString(city)]
    .filter(Boolean)
    .join(' ')
  const right = [postalCity, toTrimmedString(country)]
    .filter(Boolean)
    .join(', ')
  const subtitle = [left, right].filter(Boolean).join(', ')
  return subtitle || undefined
}

export const getPlaceTypeLabel = (type: string | undefined): string => {
  const match = placeTypes.find((item) => item.value === type)
  return match?.label ?? PLACE_TYPE_FALLBACK_LABEL
}

export const buildPlaceSearchText = (place: {
  address?: string
  city?: string
  country?: string
  name?: string
  type?: string
  zipcode?: string
}): string =>
  [
    toTrimmedString(place.name),
    toTrimmedString(place.address),
    toTrimmedString(place.zipcode),
    toTrimmedString(place.city),
    toTrimmedString(place.country),
    getPlaceTypeLabel(place.type),
  ]
    .filter(Boolean)
    .join(' ')

export const buildPlacePickerOption = (place: Place): PlacePickerOption => {
  const subtitle = formatPlaceSubtitle(place)

  return {
    label: place.name ?? 'Nom non renseigné',
    value: place.id,
    name: place.name ?? 'Nom non renseigné',
    type: place.type,
    typeLabel: getPlaceTypeLabel(place.type),
    address: place.address ?? undefined,
    zipcode: place.zipcode ?? undefined,
    city: place.city ?? undefined,
    country: place.country ?? undefined,
    location: place.location ?? undefined,
    organizationID: place.organizationID ?? undefined,
    subtitle,
    searchText: buildPlaceSearchText({
      name: place.name ?? undefined,
      type: place.type ?? undefined,
      address: place.address ?? undefined,
      zipcode: place.zipcode ?? undefined,
      city: place.city ?? undefined,
      country: place.country ?? undefined,
    }),
  }
}

export const sortPlacePickerOptions = (
  options: readonly PlacePickerOption[],
): PlacePickerOption[] =>
  [...options].sort((left, right) => {
    const typeCompare = (left.typeLabel ?? '').localeCompare(
      right.typeLabel ?? '',
    )
    if (typeCompare !== 0) return typeCompare

    const nameCompare = left.label.localeCompare(right.label)
    if (nameCompare !== 0) return nameCompare

    return (left.zipcode ?? '').localeCompare(right.zipcode ?? '')
  })

export const buildGroupedPlaceOptions = (
  options: readonly PlacePickerOption[],
): PlaceOptionGroup[] => {
  const groups = new Map<string, PlaceOptionGroup>()

  for (const option of sortPlacePickerOptions(options)) {
    const groupValue = option.type ?? UNKNOWN_PLACE_TYPE
    const groupLabel = option.typeLabel ?? PLACE_TYPE_FALLBACK_LABEL
    const currentGroup = groups.get(groupValue)

    if (currentGroup) {
      currentGroup.options.push(option)
      continue
    }

    groups.set(groupValue, {
      label: groupLabel,
      value: groupValue,
      options: [option],
    })
  }

  return [...groups.values()].sort((left, right) =>
    left.label.localeCompare(right.label),
  )
}

export const flattenGroupedPlaceOptions = (
  groups: readonly PlaceOptionGroup[] | undefined,
): PlacePickerOption[] => (groups ?? []).flatMap((group) => group.options ?? [])

export const parsePostalCityValue = (
  value: string | undefined,
): ParsedPostalCity => {
  const normalizedValue = toTrimmedString(value)
  if (!normalizedValue) return {}

  const match = normalizedValue.match(/^(\d{4,5})[\s,-]+(.+)$/u)

  if (!match) {
    return { city: normalizedValue }
  }

  return {
    zipcode: toTrimmedString(match[1]),
    city: toTrimmedString(match[2]),
  }
}

export const isValidCoordinate = (value: unknown): value is number => {
  return typeof value === 'number' && Number.isFinite(value)
}

export const buildPlaceGeoSearchText = (params: {
  address?: string
  city?: string
  country?: string
  zipcode?: string
}): string =>
  [params.address, params.zipcode, params.city, params.country]
    .map(toTrimmedString)
    .filter(Boolean)
    .join(', ')

export const buildPlaceDirectoryFilter = (
  scopeFilter: Record<string, unknown> | undefined,
  filters: PlaceDirectoryFilters,
) => {
  const clauses: Record<string, unknown>[] = []
  const search = toTrimmedString(filters.search)
  const type = toTrimmedString(filters.type)
  const city = toTrimmedString(filters.city)
  const zipcode = toTrimmedString(filters.zipcode)

  if (type) {
    clauses.push({ type: { eq: type } })
  }
  if (city) {
    clauses.push({ city: { contains: city } })
  }
  if (zipcode) {
    clauses.push({ zipcode: { contains: zipcode } })
  }
  if (search) {
    clauses.push({
      or: [
        { name: { contains: search } },
        { address: { contains: search } },
        { city: { contains: search } },
        { zipcode: { contains: search } },
      ],
    })
  }

  if (clauses.length === 0) return scopeFilter
  if (!scopeFilter) return { and: clauses }
  return {
    and: [scopeFilter, ...clauses],
  }
}
